import { computed } from 'vue';
import { useDirectoryChildrenQuery } from '../../data/useDirectoryChildrenQuery';
import { useFileManagerNavigation } from '../core/useFileManagerNavigation';
import { useFileExplorerSelection } from './useFileManagerSelection';
import { isFolderLike, type FileSystemItem } from '../../types';

export type SelectionBreakdown = {
  directoryCount: number;
  fileCount: number;
  total: number;
};

// Pure: same testability rationale as resolveDragStart/canDropAt/computeSelectionRange.
export const computeSelectionBreakdown = (
  items: FileSystemItem[],
  selectedPaths: Set<string>,
): SelectionBreakdown => {
  const selected = items.filter((item) => selectedPaths.has(item.path));
  // isFolderLike, not `item.type === 'directory'`: a selected mount row (a document library) is
  // folder-shaped and must count as a directory too — this was previously a bug (CESG-768 mount
  // refactor) where a selected library fell through to the file bucket.
  const directoryCount = selected.filter((item) => isFolderLike(item)).length;
  return { directoryCount, fileCount: selected.length - directoryCount, total: selected.length };
};

// Re-queries `useDirectoryChildrenQuery(currentPath)` with the same query key `FileExplorerView`
// already uses, so this reads the shared TanStack Query cache (no extra fetch, no new
// provide/inject needed to access "current items") crossed against the existing selection state.
export const useSelectionBreakdown = () => {
  const { currentPath } = useFileManagerNavigation();
  const { selectedPaths } = useFileExplorerSelection();
  const { data: items } = useDirectoryChildrenQuery(currentPath, true);
  return computed(() => computeSelectionBreakdown(items.value ?? [], selectedPaths.value));
};
